import { DataType, ParameterValueType } from '@tableau/embedding-api';
import { TABLEAU_FILTER_FIELDS } from 'features/frontoffice/component/reportTableauFilters';

const TABLEAU_PARAMETER_VALUE_ALIASES = {
  [TABLEAU_FILTER_FIELDS.dateBasis]: {
    1: ['1', 'Month Paid'],
    2: ['2', 'Month Incurred'],
    paid: [1, '1', 'Month Paid'],
    incurred: [2, '2', 'Month Incurred']
  }
};

const normalizeTableauName = (value) => String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

const getTableauFieldAliases = (fieldName) => {
  const normalizedFieldName = String(fieldName ?? '').trim();

  if (!normalizedFieldName) {
    return [];
  }

  const spacedName = normalizedFieldName.replace(/_/g, ' ');
  const titleCaseName = spacedName.replace(/\b\w/g, (letter) => letter.toUpperCase());

  return [
    normalizedFieldName,
    spacedName,
    titleCaseName,
    spacedName.toLowerCase(),
    titleCaseName.toLowerCase()
  ].filter((value, index, values) => value && values.indexOf(value) === index);
};

const parseTableauDateValue = (value) => {
  if (value instanceof Date || value === null || value === undefined || value === '') {
    return value;
  }

  const normalizedValue = String(value).trim();
  const dateParts = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (dateParts) {
    const [, yearValue, monthValue, dayValue] = dateParts;
    return new Date(Number(yearValue), Number(monthValue) - 1, Number(dayValue));
  }

  const parsedDate = new Date(normalizedValue);
  return Number.isNaN(parsedDate.getTime()) ? value : parsedDate;
};

const getTableauDataValueCandidate = (dataValue) => {
  if (!dataValue) {
    return undefined;
  }

  if (dataValue.nativeValue !== null && dataValue.nativeValue !== undefined) {
    return dataValue.nativeValue;
  }

  if (dataValue.value !== null && dataValue.value !== undefined) {
    return dataValue.value;
  }

  return undefined;
};

const findMatchingAllowableParameterValue = (rawValue, parameter) => {
  if (parameter?.allowableValues?.type !== ParameterValueType.List) {
    return undefined;
  }

  const normalizedRawValue = String(rawValue).trim().toLowerCase();
  const allowableValues = parameter.allowableValues.allowableValues || [];

  for (const allowableValue of allowableValues) {
    const candidates = [
      allowableValue?.formattedValue,
      allowableValue?.value,
      allowableValue?.nativeValue
    ].filter((candidate) => candidate !== null && candidate !== undefined);

    const match = candidates.find(
      (candidate) => String(candidate).trim().toLowerCase() === normalizedRawValue
    );

    if (match !== undefined) {
      return getTableauDataValueCandidate(allowableValue);
    }
  }

  return undefined;
};

const getTableauParameterNameCandidates = (fieldName, parameters) => {
  const aliasNames = getTableauFieldAliases(fieldName);
  const normalizedAliasNames = aliasNames.map(normalizeTableauName);
  const matchingParameters = parameters.filter((parameter) => {
    const normalizedParameterName = normalizeTableauName(parameter.name);

    return normalizedAliasNames.some(
      (normalizedAliasName) =>
        normalizedParameterName === normalizedAliasName
        || normalizedParameterName.includes(normalizedAliasName)
        || normalizedAliasName.includes(normalizedParameterName)
    );
  });

  const matchedNames = matchingParameters.map((parameter) => parameter.name);

  return [...matchedNames, ...aliasNames].filter(
    (candidate, index, candidates) => candidate && candidates.indexOf(candidate) === index
  );
};

const appendUniqueCandidate = (candidates, value) => {
  const candidateKey = `${typeof value}:${value instanceof Date ? value.toISOString() : String(value)}`;

  if (!candidates.some((candidate) => candidate.key === candidateKey)) {
    candidates.push({
      key: candidateKey,
      value
    });
  }
};

const appendAliasedTableauParameterValues = (candidates, fieldName, rawValue) => {
  const fieldAliases = TABLEAU_PARAMETER_VALUE_ALIASES[fieldName];

  if (!fieldAliases) {
    return;
  }

  const aliasValues = fieldAliases[String(rawValue).trim().toLowerCase()] || fieldAliases[rawValue];

  if (!aliasValues) {
    return;
  }

  aliasValues.forEach((aliasValue) => {
    appendUniqueCandidate(candidates, aliasValue);
  });
};

const appendTypedTableauParameterValues = (candidates, rawValue, dataType) => {
  switch (dataType) {
    case DataType.Bool: {
      if (typeof rawValue === 'boolean') {
        appendUniqueCandidate(candidates, rawValue);
        break;
      }

      const normalizedValue = String(rawValue).trim().toLowerCase();

      if (['true', '1', 'yes'].includes(normalizedValue)) {
        appendUniqueCandidate(candidates, true);
      }

      if (['false', '0', 'no'].includes(normalizedValue)) {
        appendUniqueCandidate(candidates, false);
      }

      break;
    }
    case DataType.Int: {
      const parsedValue = Number.parseInt(String(rawValue), 10);

      if (!Number.isNaN(parsedValue)) {
        appendUniqueCandidate(candidates, parsedValue);
      }

      break;
    }
    case DataType.Float: {
      const parsedValue = Number.parseFloat(String(rawValue));

      if (!Number.isNaN(parsedValue)) {
        appendUniqueCandidate(candidates, parsedValue);
      }

      break;
    }
    case DataType.Date:
    case DataType.DateTime:
      appendUniqueCandidate(candidates, parseTableauDateValue(rawValue));
      break;
    default:
      appendUniqueCandidate(candidates, String(rawValue));
  }
};

const appendRawTableauParameterValues = (candidates, rawValue) => {
  if (typeof rawValue === 'number') {
    appendUniqueCandidate(candidates, rawValue);
    appendUniqueCandidate(candidates, String(rawValue));
    return;
  }

  if (typeof rawValue === 'string') {
    appendUniqueCandidate(candidates, rawValue);

    const parsedInteger = Number.parseInt(rawValue, 10);

    if (!Number.isNaN(parsedInteger) && String(parsedInteger) === rawValue.trim()) {
      appendUniqueCandidate(candidates, parsedInteger);
    }
  }
};

const getTableauParameterValueCandidates = (fieldName, rawValue, parameter) => {
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return [rawValue];
  }

  const candidates = [];

  appendAliasedTableauParameterValues(candidates, fieldName, rawValue);

  const allowableMatch = findMatchingAllowableParameterValue(rawValue, parameter);

  if (allowableMatch !== undefined) {
    appendUniqueCandidate(candidates, allowableMatch);
  }

  appendTypedTableauParameterValues(candidates, rawValue, parameter?.dataType);
  appendRawTableauParameterValues(candidates, rawValue);

  return candidates.map((candidate) => candidate.value);
};

export {
  getTableauFieldAliases,
  getTableauParameterNameCandidates,
  getTableauParameterValueCandidates,
  normalizeTableauName
};